import React, { useEffect, useMemo, useState } from 'react';
import MyAppText from '../components/MyAppText';
import { View, Image, ActivityIndicator } from 'react-native';
import { translate } from '../services/translate';
import PasswordInput from '../components/PasswordInput';
import PasswordValidator from 'password-validator';
import Toast from 'react-native-toast-message';
import { ClientError, NotFound } from '../services/central-api/base-service';
import { WindowInformation } from '../services/window-information';
import { centralAPI } from '../services/central-api';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import Container from '../components/Container';
import Card from '../components/Card';
import CustomButton from '../components/CustomButton';

interface ResetPasswordProps {
    route: {
        params?: {
            token?: string;
        };
    };
}

export default function ResetPassword({ route }: ResetPasswordProps) {
    const { styles, theme } = useStyles(styleSheet);

    const windowInfo = WindowInformation();
    const [isFormValid, setIsFormValid] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isValidMin, setIsValidMin] = useState(false);
    const [isValidDigits, setIsValidDigits] = useState(false);
    const [isValidUppercase, setIsValidUppercase] = useState(false);
    const [isValidLowercase, setIsValidLowercase] = useState(false);
    const isSmartSampa = useMemo(() => window.location.hostname.includes('smartsampa'), [window.location]);

    const validator = new PasswordValidator();
    validator.is().min(8).has().uppercase().has().lowercase().has().digits(2);
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');

    useEffect(() => {
        const missingValidations = validator.validate(password, { list: true }) as string[];
        setIsValidMin(!missingValidations.includes('min'));
        setIsValidUppercase(!missingValidations.includes('uppercase'));
        setIsValidLowercase(!missingValidations.includes('lowercase'));
        setIsValidDigits(!missingValidations.includes('digits'));
    }, [password]);

    useEffect(() => {
        if (password === '' || confirmPassword === '' || confirmPassword !== password) {
            return setIsFormValid(false);
        }

        if (!validator.validate(password)) {
            return setIsFormValid(false);
        }

        return setIsFormValid(true);
    }, [password, confirmPassword]);

    async function resetPassword() {
        try {
            setLoading(true);
            await centralAPI.resetPassword(
                password,
                confirmPassword,
                route.params?.token || ''
            );
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            window.location.href = '/login';

        } catch (err) {
            if (err instanceof NotFound) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate('TokenNotFound'),
                });
            }

            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }
            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        } finally {
            setLoading(false);
        }
    }

    return (
        <Container style={{ justifyContent: 'center', alignItems: 'center' }}>
            <Card>
                <View style={styles.imageView}>
                    {isSmartSampa ? (windowInfo.isMobileOrTablet
                        ? <Image style={styles.image} source={require(`../../assets/big_smartsampa_logo_mobile_${UnistylesRuntime.themeName}.png`)} />
                        : <Image style={styles.image} source={require(`../../assets/big_smartsampa_logo_${UnistylesRuntime.themeName}.png`)} />
                    ) :
                        <Image style={styles.image} source={require(`../../assets/SentinelX_logo_${UnistylesRuntime.themeName}.png`)} />
                    }
                </View>
                <View style={styles.formContainer}>
                    <View style={{ gap: 10 }}>
                        <MyAppText style={styles.headerText}>{translate('passwordRequirements')}</MyAppText>
                        <View style={{ gap: 5 }}>
                            <MyAppText style={[styles.label, password === '' ? null : isValidMin ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeast8Characters')}`}</MyAppText>
                            <MyAppText style={[styles.label, password === '' ? null : isValidDigits ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeast2Digits')}`}</MyAppText>
                            <MyAppText style={[styles.label, password === '' ? null : isValidUppercase ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeastOneUppercase')}`}</MyAppText>
                            <MyAppText style={[styles.label, password === '' ? null : isValidLowercase ? styles.valid : styles.invalid]}>{`\u2022 ${translate('atLeastOneLowercase')}`}</MyAppText>
                        </View>
                    </View>
                    <PasswordInput
                        label={translate('newPassword')}
                        placeholder=''
                        value={password}
                        onChangeText={setPassword}
                        invalid={() => password === ''}
                    />
                    <PasswordInput
                        label={translate('confirmNewPassword')}
                        placeholder=''
                        value={confirmPassword}
                        onChangeText={setConfirmPassword}
                        invalid={() => password != confirmPassword}
                        errorMessage={translate('passwordsNotEqual')}
                    />

                    {loading ?
                        <View style={{ height: 40 }}>
                            <ActivityIndicator size='small' color={theme.colors.iconColor} />
                        </View>
                        : <CustomButton
                            onPress={() => resetPassword()}
                            text={translate('save')}
                            type='primary'
                            disabled={!isFormValid}
                        />
                    }
                </View>
            </Card>
        </Container>
    );
}

const styleSheet = createStyleSheet((theme) => ({
    headerText: {
        fontSize: 24,
        color: theme.colors.labelColor,
        fontWeight: 'bold'
    },
    label: {
        color: theme.colors.labelColor,
    },
    valid: {
        color: theme.colors.successes['300'],
    },
    invalid: {
        color: theme.colors.errors['300'],
    },
    formContainer: {
        flexDirection: 'column',
        gap: 30,
        maxWidth: 375,
        width: '100%'
    },
    image: {
        width: '100%',
        resizeMode: 'contain',
        height: '100%',
    },
    imageView: {
        width: 260,
        height: 40,
    },
}));
